import { isList, isObjectLike } from './guards';
import type { JsonObject, JsonValue } from './types';

/** True for a value made only of strings, finite numbers, booleans, null, arrays and plain objects of those. */
export function isJsonValue<T>(value: T): value is T & JsonValue {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(isJsonValue);
  if (typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return false;
  return Object.values(value).every(isJsonValue);
}

/** A JSON object: not text, a number, a boolean, null or a list. */
export function isJsonObject(value: JsonValue): value is JsonObject {
  return isObjectLike(value) && !isList(value);
}

export type JsonParseResult =

  | { readonly ok: true; readonly value: JsonValue }
  | { readonly ok: false; readonly reason: string };

/** Parses text into a `JsonValue`; the failure branch names the problem instead of throwing. */
export function parseJson(text: string): JsonParseResult {
  let parsed: JsonValue;
  try {
    const candidate: unknown = JSON.parse(text);
    if (!isJsonValue(candidate)) return { ok: false, reason: 'not a JSON value' };
    parsed = candidate;
  } catch (cause) {
    return { ok: false, reason: cause instanceof Error ? cause.message : 'invalid JSON' };
  }
  return { ok: true, value: parsed };
}
